import React, { FC } from 'react';
import styled from 'styled-components';
import theme from '../theme/theme';

export type ZSwitchVariant = 'default' | 'ai';

export interface ZSwitchProps {
    ariaLabel?: string;
    checked?: boolean;
    className?: string;
    disabled?: boolean;
    id?: string;
    name?: string;
    onChange?: (checked: boolean, event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>, id?: string) => void;
    variant?: ZSwitchVariant;
}

const SwitchWrapper = styled.div<{ $checked?: boolean; $variant: ZSwitchVariant }>`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    border-radius: 50px;
    background: transparent;
`;

const SwitchButton = styled.button<{ $checked?: boolean }>`
    position: relative;
    width: 32px;
    height: 16px;
    margin: 0;
    padding: 0;
    border: 0;
    border-radius: 50px;
    background: transparent;
    cursor: pointer;
    box-shadow: 0px 0px 2px 0px rgba(0, 0, 0, 0.3);
    pointer-events: auto;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.55;
    }
`;

const SwitchBackground = styled.span<{ $checked?: boolean; $variant: ZSwitchVariant }>`
    position: absolute;
    inset: 0;
    border-radius: inherit;
    transition: background 0.3s ease;
    background: ${({ $checked, $variant }) => {
        if (!$checked) return '#888';
        if ($variant === 'ai') return 'linear-gradient(90deg, rgba(59, 130, 246, 0.7) 3.75%, rgba(139, 92, 246, 0.7) 103.75%)';
        return `color-mix(in srgb, ${theme.app.secondary} 80%, white)`;
    }};
`;

const SwitchHandle = styled.span<{ $checked?: boolean; $variant: ZSwitchVariant }>`
    position: absolute;
    top: 0;
    left: ${(props) => (props.$checked ? '16px' : '0')};
    width: 16px;
    height: 16px;
    border-radius: 50%;
    box-sizing: border-box;
    transition: all 0.3s ease;
    background: ${({ $checked, $variant }) => {
        if (!$checked) return '#fff';
        if ($variant === 'ai') return 'linear-gradient(90deg, #3B82F6 3.75%, #8B5CF6 103.75%)';
        return theme.app.secondary;
    }};
    border: ${({ $checked }) => ($checked ? 'none' : `1px solid #888`)};
    z-index: 1;
`;

export const ZSwitch: FC<ZSwitchProps> = ({ variant = 'default', ariaLabel, checked, className, disabled, id, name, onChange }) => {
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (disabled) return;
        onChange?.(!checked, event, id);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (disabled || (event.key !== 'Enter' && event.key !== ' ')) return;

        event.preventDefault();
        onChange?.(!checked, event, id);
    };

    return (
        <SwitchWrapper $checked={checked} $variant={variant} className={className}>
            <SwitchButton
                id={id}
                name={name}
                type='button'
                role='switch'
                aria-checked={!!checked}
                aria-label={ariaLabel}
                disabled={disabled}
                $checked={checked}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
            >
                <SwitchBackground $checked={checked} $variant={variant} />
                <SwitchHandle $checked={checked} $variant={variant} />
            </SwitchButton>
        </SwitchWrapper>
    );
};
